import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { processNoteContent, NOTE_PROSE_CLASSES } from '../utils/noteContent';

function NoteDisplay() {
  const params = useParams();
  // Handle both noteName (old route) and slug (new routes) parameter names
  const slug = params.noteName || params.slug;
  const navigate = useNavigate();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [footerHtml, setFooterHtml] = useState(null);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (token) {
        try {
          const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAdmin(response.data.success === true);
        } catch (err) {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };
    
    checkAdmin();
  }, []);

  // Fetch config for footer (and other config-driven UI)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/config`);
        if (response.data.success && response.data.data) {
          const footer = response.data.data.footer;
          if (footer && typeof footer === 'string') {
            setFooterHtml(footer);
          }
        }
      } catch (err) {
        console.debug('Could not load config for footer', err);
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    const fetchNote = async () => {
      // First, check if this path is a redirect
      const currentPath = `/${slug}`;
      
      // Skip redirect check for known note routes and the 404 slug itself
      if (slug === '404' || currentPath.startsWith('/note/') || currentPath.startsWith('/notes/')) {
        // Continue to normal note loading
      } else {
        try {
          // Check if this path has a redirect
          const pathWithoutSlash = currentPath.startsWith('/') ? currentPath.substring(1) : currentPath;
          const redirectResponse = await axios.get(`${API_BASE_URL}/redirect/check/${pathWithoutSlash}`);
          
          if (redirectResponse.data.success && redirectResponse.data.exists) {
            const redirect = redirectResponse.data.data;
            
            // If password is required, redirect to server (which will show password form)
            if (redirect.hasPassword) {
              // Get the server URL (remove /api from API_BASE_URL)
              const serverUrl = API_BASE_URL.replace('/api', '');
              window.location.href = `${serverUrl}${currentPath}`;
              return;
            }
            
            // No password required - redirect directly to target URL
            window.location.href = redirect.targetUrl;
            return;
          }
        } catch (redirectErr) {
          // If redirect check fails, continue to normal note loading
          console.debug('No redirect found for path:', currentPath);
        }
      }
      
      // No redirect found, proceed with normal note loading
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/note/${slug}`);
        if (response.data.success) {
          setNote(response.data.data);
        } else {
          setError(response.data.error || 'Failed to load note');
        }
      } catch (err) {
        console.error('Error fetching note:', err);
        if (err.response?.status === 404) {
          // Redirect to 404 note if current note is not 404
          if (slug !== '404') {
            navigate('/note/404', { replace: true });
            return;
          } else {
            setError('Note not found');
          }
        } else {
          setError(err.response?.data?.error || err.message || 'Failed to load note');
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchNote();
    }
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading note...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Error</h2>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!note) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center">
          <p className="text-gray-600">Note not found</p>
        </div>
      </div>
    );
  }

  const processedContent = processNoteContent(note.content);

  // Footer: use config footer HTML if set, replace __YEAR__ with current year; else fallback
  const processedFooterHtml = footerHtml != null
    ? processNoteContent(
        String(footerHtml).replace(/__YEAR__/g, String(new Date().getFullYear()))
      )
    : null;
  const defaultFooterHtml = `<div class="max-w-7xl mx-auto px-4 py-6"><p class="text-center text-gray-600 text-sm">© ${new Date().getFullYear()} Digital Designs Store. All rights reserved.</p></div>`;

  return (
    <>
      {/* Admin Links */}
      {isAdmin && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="flex items-center gap-4 text-sm">
              <button
                onClick={() => navigate('/admin/settings', { state: { activeTab: 'notes-editor' } })}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                All Notes
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={() => {
                  // Use slug to find the note (note object from getPageBySlug doesn't include name)
                  // SettingsPage will match by slug if name doesn't match
                  const editNote = note?.slug || slug;
                  navigate('/admin/settings', { state: { activeTab: 'notes-editor', editNote } });
                }}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Edit Note
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          {!note.hideTitle && (
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{note.title}</h1>
          )}
          {!note.hideTags && note.tags && note.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {note.tags.map(tag => (
                <a
                  key={tag}
                  href={`/notes?tag=${encodeURIComponent(tag)}`}
                  className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full hover:bg-blue-200 transition-colors"
                >
                  {tag.toLowerCase()}
                </a>
              ))}
            </div>
          )}
          <div
            className={NOTE_PROSE_CLASSES}
            dangerouslySetInnerHTML={{ __html: processedContent }}
          />
        </div>
      </div>
      
      {/* Footer - from config.json footer, or default */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div
          className="footer-content [&_a]:text-blue-600 [&_a:hover]:underline"
          dangerouslySetInnerHTML={{
            __html: processedFooterHtml != null ? processedFooterHtml : defaultFooterHtml
          }}
        />
      </footer>
    </>
  );
}

export default NoteDisplay;
